import React, { useState, useEffect, useRef, useMemo } from 'react'; // Added useRef and useMemo, removed useCallback
import {
	Box,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	TableContainer,
	Tooltip,
	Grid,
	GridItem,
	HStack, // Keep
	VStack,
	FormControl,
	Select,
	DrawerContent,
	DrawerCloseButton,
	DrawerBody,
	DrawerOverlay,
	Drawer,
	Text,
	CardBody,
	Stack,
	Heading,
	StackDivider,
	Card,
	Input,
	Spinner,
	Flex,
	Button,
	Alert,
	AlertIcon,
	AlertTitle,
	AlertDescription,
} from '@chakra-ui/react';
import VideoPlayer from '../components/VideoPlayer';
import { useColorMode } from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/hooks';
import { decompressToString } from '../utils/decompressor';
import { FaVideo } from 'react-icons/fa';
import { RiArrowUpDownFill } from 'react-icons/ri';


// Helper function to convert platform byte to string
function getPlatformString(platform) {
	const platformValue = typeof platform === 'string' ? parseInt(platform) : platform;
	switch(platformValue) {
		case 0: return 'iOS';
		case 1: return 'Android';
		case 2: return 'Editor';
		default: return platform || 'Unknown';
	}
}

const eventTypes = [
	{ id: 0, name: 'All' },
	{ id: 1, name: 'Analytics' },
	{ id: 2, name: 'UnityLog' },
];

// Server ID to URL mapping
const SERVER_MAPPINGS = {
	1: 'http://216.128.128.145:8443',
	2: 'http://localhost:8081',
};

// Function to construct download URL from server ID, session ID, and archive ID
function getArchiveDownloadUrl(serverId, sessionId, archiveId) {
	const baseUrl = SERVER_MAPPINGS[serverId];
	if (!baseUrl) {
		console.error(`No mapping found for server ID: ${serverId}`);
		return null;
	}
	// Remove any file extension from archiveId since the server will add it
	const cleanArchiveId = archiveId.replace(/\.(bin|zst)$/, '');
	// Add timestamp to prevent browser caching
	return `${baseUrl}/files/${sessionId}/${cleanArchiveId}?t=${Date.now()}`;
}

// Function to read a 7-bit encoded integer (LEB128) as used by C# BinaryWriter
function read7BitEncodedInt(dataView, offset) {
	let value = 0;
	let shift = 0;
	let pos = offset;
	let byte;
	
	do {
		if (shift === 35) {
			throw new Error('7-bit encoded int is too large');
		}
		
		if (pos >= dataView.byteLength) {
			throw new Error('Unexpected end of data while reading 7-bit encoded int');
		}
		
		byte = dataView.getUint8(pos++);
		value |= (byte & 0x7F) << shift;
		shift += 7;
	} while ((byte & 0x80) !== 0);
	
	return { value, bytesRead: pos - offset };
}

// Function to read a C# string (7-bit encoded length + UTF8 bytes)
function readString(dataView, offset) {
	const { value: length, bytesRead: lengthBytes } = read7BitEncodedInt(dataView, offset);
	let pos = offset + lengthBytes;
	
	if (length === 0) {
		return { value: '', bytesRead: lengthBytes };
	}
	
	const stringBytes = new Uint8Array(dataView.buffer, dataView.byteOffset + pos, length);
	const value = new TextDecoder().decode(stringBytes);
	
	return { value, bytesRead: lengthBytes + length };
}

// Function to parse individual LogData (without SessionID)
function parseBinaryLogData(dataView, offset) {
	let pos = offset;
	
	// Read Source (1 byte)
	const source = dataView.getUint8(pos++);
	
	// Read Type (1 byte)
	const type = dataView.getUint8(pos++);
	
	// Read TypeVersion (1 byte)
	const typeVersion = dataView.getUint8(pos++);
	
	// Read NativeLogType (1 byte)
	const nativeLogType = dataView.getUint8(pos++);
	
	// Read EventNumber (int32)
	const eventNumber = dataView.getInt32(pos, true);
	pos += 4;
	
	// Read FrameNumber (int32)
	const frameNumber = dataView.getInt32(pos, true);
	pos += 4;
	
	// Read LogString (string - 7-bit encoded length)
	const logStringResult = readString(dataView, pos);
	const logString = logStringResult.value;
	pos += logStringResult.bytesRead;
	
	// Read StackTrace (string - 7-bit encoded length)
	const stackTraceResult = readString(dataView, pos);
	const stackTrace = stackTraceResult.value;
	pos += stackTraceResult.bytesRead;
	
	// Read Timestamp (float64)
	const timestamp = dataView.getFloat64(pos, true);
	pos += 8;
	
	// Convert log type to string
	const logTypeMap = {
		0: 'Trace',
		1: 'Info',
		2: 'Debug',
		3: 'Warning',
		4: 'Error',
		5: 'Exception',
		6: 'Assert',
		7: 'Fatal'
	};
	
	return {
		event_number: eventNumber,
		log_type: logTypeMap[nativeLogType] || 'Unknown',
		log_string: logString,
		stack_trace: stackTrace,
		timestamp: timestamp,
		frame_number: frameNumber,
		bytesRead: pos - offset // Return how many bytes were read
	};
}

// Function to parse BinaryEventArrayData
async function parseBinaryEventArrayData(dataView) {
	let pos = 0;
	
	// Read Source (1 byte)
	const source = dataView.getUint8(pos++);
	
	// Read Type (1 byte)
	const type = dataView.getUint8(pos++);
	
	// Read TypeVersion (1 byte)
	const typeVersion = dataView.getUint8(pos++);
	
	// Read SessionId (string - 7-bit encoded length)
	console.log(`Reading SessionId at position ${pos}, next few bytes:`, Array.from(new Uint8Array(dataView.buffer, dataView.byteOffset + pos, Math.min(10, dataView.byteLength - pos))).map(b => b.toString(16).padStart(2, '0')).join(' '));
	const sessionIdResult = readString(dataView, pos);
	const sessionId = sessionIdResult.value;
	pos += sessionIdResult.bytesRead;
	console.log(`SessionId: "${sessionId}", bytes read: ${sessionIdResult.bytesRead}`);
	
	// Read IsOnlyLogs (bool - 1 byte)
	const isOnlyLogs = dataView.getUint8(pos++) !== 0;
	
	// Read IsOnlyEvents (bool - 1 byte)
	const isOnlyEvents = dataView.getUint8(pos++) !== 0;
	
	// Read IsCompressed (bool - 1 byte)
	const isCompressed = dataView.getUint8(pos++) !== 0;
	
	// Read UncompressedSize (int32)
	const uncompressedSize = dataView.getInt32(pos, true);
	pos += 4;
	
	// Read CompressedSize (int32)
	const compressedSize = dataView.getInt32(pos, true);
	pos += 4;
	
	// Read Timestamp (float64)
	const timestamp = dataView.getFloat64(pos, true);
	pos += 8;
	
	// Read ArrayItemCount (int32)
	const itemCount = dataView.getInt32(pos, true);
	pos += 4;
	
	// Read Data.Count (the actual list count)
	const listCount = dataView.getInt32(pos, true);
	pos += 4;
	
	console.log(`BinaryEventArrayData: sessionId=${sessionId}, arrayItemCount=${itemCount}, listCount=${listCount}, isOnlyLogs=${isOnlyLogs}, isCompressed=${isCompressed}`);
	
	const logs = [];
	
	// Parse each item in the Data list
	for (let i = 0; i < listCount; i++) {
		// Read item length
		const itemLength = dataView.getInt32(pos, true);
		pos += 4;
		
		if (itemLength > 0) {
			// Read the byte array
			const itemBytes = new Uint8Array(dataView.buffer, dataView.byteOffset + pos, itemLength);
			
			try {
				let dataBytes = itemBytes;
				
				// Decompress if needed
				if (isCompressed) {
					const { decompressUint8Array } = await import('../utils/decompressor');
					dataBytes = await decompressUint8Array(itemBytes);
				}
				
				// Create DataView for the item
				const itemDataView = new DataView(dataBytes.buffer, dataBytes.byteOffset, dataBytes.byteLength);
				
				// Check the type byte to determine if it's a log or event
				if (dataBytes.length >= 2) {
					const itemType = itemDataView.getUint8(1); // Type is at position 1
					
					if (itemType === 0x02) { // BinaryEventType.Log = 2
						// Parse as LogData
						const log = parseBinaryLogData(itemDataView, 0);
						log.session_id = sessionId || 'unknown'; // Add session ID to the log
						logs.push(log);
					} else {
						console.log(`Skipping non-log item ${i} with type ${itemType}`);
					}
				}
			} catch (e) {
				console.error(`Error parsing/decompressing item ${i}:`, e);
			}
		}
		
		pos += itemLength;
	}
	
	return logs;
}

// Function to download and decompress an archive
// Function to download and decode video file
async function downloadAndDecodeVideo(serverId, sessionId, videoId) {
	const url = getArchiveDownloadUrl(serverId, sessionId, videoId);
	if (!url) {
		throw new Error(`No server mapping for server ID ${serverId}`);
	}

	console.log(`Downloading video from: ${url}`);
	
	try {
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(`HTTP ${response.status}: ${response.statusText}`);
		}
		
		const arrayBuffer = await response.arrayBuffer();
		const uint8Array = new Uint8Array(arrayBuffer);
		
		console.log(`Downloaded video ${videoId}, size: ${uint8Array.length} bytes`);
		
		// Import and use the video decoder
		const { default: videoDecoder } = await import('../wasm/videoDecoder');
		
		// Initialize decoder if needed
		await videoDecoder.init();
		
		// Decode the video
		const decodedData = await videoDecoder.decodeVideo(uint8Array);
		
		console.log(`Successfully decoded video ${videoId}, ${decodedData.frames.length} frames`);
		
		return decodedData;
	} catch (error) {
		console.error('Error downloading/decoding video:', error);
		throw error;
	}
}

async function downloadAndDecompressArchive(serverId, sessionId, archiveId, onLogsReceived) {
	const url = getArchiveDownloadUrl(serverId, sessionId, archiveId);
	console.log(`Downloading archive: serverId=${serverId}, sessionId=${sessionId}, archiveId=${archiveId}`);
	console.log(`Download URL: ${url}`);
	
	if (!url) {
		alert('Unable to determine download URL for this archive');
		return;
	}
	
	try {
		// Fetch the compressed binary data
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}
		
		// Get the data as ArrayBuffer
		const arrayBuffer = await response.arrayBuffer();
		const uint8Array = new Uint8Array(arrayBuffer);
		
		console.log(`Downloaded ${uint8Array.length} bytes for archive ${archiveId}`);
		console.log('First 16 bytes (hex):', Array.from(uint8Array.slice(0, 16)).map(b => b.toString(16).padStart(2, '0')).join(' '));
		console.log('First 4 bytes as string:', new TextDecoder().decode(uint8Array.slice(0, 4)));
		
		// Check if it's ZSTD compressed (magic number: 28 b5 2f fd)
		const isZstd = uint8Array[0] === 0x28 && uint8Array[1] === 0xb5 && uint8Array[2] === 0x2f && uint8Array[3] === 0xfd;
		console.log('Is ZSTD compressed:', isZstd);
		
		// The file might be a raw binary file with .zst extension but not actually compressed
		// Let's check the BinaryEventArrayData type byte
		if (!isZstd && uint8Array.length > 2 && uint8Array[1] === 0x04) {
			console.log('Data appears to be uncompressed BinaryEventArrayData');
		}
		
		let binaryData;
		
		// Only try to decompress if it's actually ZSTD compressed
		if (isZstd) {
			try {
				// Import the decompressUint8Array function which returns bytes
				const { decompressUint8Array } = await import('../utils/decompressor');
				const decompressed = await decompressUint8Array(uint8Array);
				
				// decompressUint8Array returns Uint8Array directly
				binaryData = decompressed;
				console.log(`Decompressed to ${binaryData.length} bytes`);
			} catch (decompressError) {
				console.error('Decompression failed:', decompressError);
				alert('Failed to decompress ZSTD data: ' + decompressError.message);
				return;
			}
		} else {
			// Data is not ZSTD compressed, use it as-is
			binaryData = uint8Array;
		}
		
		// Create DataView for parsing
		const dataView = new DataView(binaryData.buffer, binaryData.byteOffset, binaryData.byteLength);
		
		// Parse using the new BinaryEventArrayData parser
		const logs = await parseBinaryEventArrayData(dataView);
		
		// Call the callback with the parsed logs
		if (onLogsReceived && logs.length > 0) {
			onLogsReceived(logs);
		}
		
		console.log(`Successfully decompressed archive ${archiveId}, found ${logs.length} logs`);
	} catch (error) {
		console.error('Error downloading/decompressing archive:', error);
		alert('Failed to download or decompress archive: ' + error.message);
	}
}

function PlayerLogData({ userToken, selectedGameId }) { // Renamed from LogData
	const [sessionList, setSessionList] = useState([]);
	const { colorMode, setColorMode } = useColorMode();
	const [selectedSessionId, setSelectedSessionId] = useState(null);
	const webSocket = useRef(null); // Changed from useState to useRef
	const [playerId, setPlayerId] = useState(''); // Added
	const [sessionIdInput, setSessionIdInput] = useState(''); // Added
	// Removed: isUserSpecific, userName, deviceIds, userList states

	useEffect(() => {
		setColorMode('dark');
	}, []);

	// Removed: useEffect for fetchUserList

	const closeWebSocket = () => {
		if (webSocket.current) {
			webSocket.current.close();
			webSocket.current = null; 
		}
	};

	// connectWebSocket is NOT wrapped in useCallback anymore
	const connectWebSocket = (currentPlayerId, currentSessionIdInput) => {
		// Use hard-coded WebSocket URL provided by backend for now (JWT auth disabled)
		//const endpoint = 'ws://localhost:8081/ws';
		const endpoint = 'ws://hub.alictuscloud.com:8081/ws';
		webSocket.current = new WebSocket(endpoint); // Use webSocket.current

		webSocket.current.onopen = () => {
			console.log('WebSocket connected successfully');
			const filters = {};
			if (currentSessionIdInput) {
			  filters.session_id = currentSessionIdInput;
			} else if (currentPlayerId) {
			  filters.player_id = currentPlayerId;
			}

			const subscribeMsg = {
				type: 'subscribe_game_sessions',
				payload: {
					filters,
					limit: 60,
					sort_by: 'timestamp',
					sort_order: 'desc',
				},
			};
			console.log('Sending subscribe message:', subscribeMsg);
			webSocket.current.send(JSON.stringify(subscribeMsg));
		};

		webSocket.current.onmessage = (event) => {
			try {
				const incoming = JSON.parse(event.data);
				if (incoming.type === 'game_session_list' && incoming.payload?.sessions) {
					console.log('Received sessions:', incoming.payload.sessions);
					setSessionList(incoming.payload.sessions);
					if (currentSessionIdInput && incoming.payload.sessions.length > 0) {
						const foundSession = incoming.payload.sessions.find(s => s.session_id === currentSessionIdInput);
						if (foundSession) {
							setSelectedSessionId(currentSessionIdInput);
						} else if (incoming.payload.sessions.length === 1 && !currentPlayerId) {
							setSelectedSessionId(incoming.payload.sessions[0].session_id);
						}
					}
				} else {
					console.warn('Unhandled websocket message:', incoming);
				}
			} catch (err) {
				console.error('Error parsing websocket message', err);
			}
		};

		webSocket.current.onclose = (event) => {
			console.log('WebSocket is closed:', event);
		};

		webSocket.current.onerror = (error) => {
			console.error('WebSocket error:', error);
		};
	};

	// Removed: Initial connection useEffect that used isUserSpecific

	// Added: handleFetchLogs function
	const handleFetchLogs = () => {
	  setSessionList([]); 
	  setSelectedSessionId(null); 
	  closeWebSocket(); 
	  connectWebSocket(playerId, sessionIdInput); 
	  
	  // Update URL with current parameters
	  const params = new URLSearchParams();
	  params.set('view', 'player-logs');
	  if (sessionIdInput) {
	    params.set('sessionId', sessionIdInput);
	  }
	  if (playerId) {
	    params.set('playerId', playerId);
	  }
	  
	  // Use replaceState to update URL without adding to browser history
	  window.history.replaceState(null, '', '?' + params.toString());
	};

	// Added: useEffect for component unmount cleanup
	useEffect(() => {
		return () => {
			if (webSocket.current) {
				webSocket.current.close();
			}
		};
	}, []);

	// Check URL params on mount to auto-populate fields
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const urlSessionId = params.get('sessionId');
		const urlPlayerId = params.get('playerId');
		
		if (urlSessionId || urlPlayerId) {
			// Set the fields based on URL params
			if (urlSessionId) {
				setSessionIdInput(urlSessionId);
			}
			if (urlPlayerId) {
				setPlayerId(urlPlayerId);
			}
			
			// Auto-fetch logs after a small delay to ensure state is set
			setTimeout(() => {
				setSessionList([]);
				setSelectedSessionId(null);
				closeWebSocket();
				connectWebSocket(urlPlayerId || '', urlSessionId || '');
			}, 100);
		}
	}, []); // Only run on mount

	// Removed: handleListSessions
	// Removed: handleResetSessions

	const SelectedSessionRow = ({ sessionId }) => (
		<Tbody key={sessionId}>
			<Tr>
				<Td colSpan={9}>
					<VStack spacing={1} align="stretch">
						<TabbedSessionInfo userToken={userToken} selectedSessionId={sessionId} />
					</VStack>
				</Td>
			</Tr>
		</Tbody>
	);

	return (
		<Box border={'2px'} borderRadius={'5px'} borderColor={'gray.600'} fontSize={'14px'}>
			<h2>
				<Box as="span" flex="1" textAlign="left">
					{/* Added input fields and button */}
					<HStack spacing={4} mb={4} mt={4} ml={2} mr={2}> {/* Added some margin for better spacing */}
						<FormControl>
							<Input 
							placeholder="Player ID" 
							value={playerId} 
							onChange={(e) => setPlayerId(e.target.value)} 
							/>
						</FormControl>
						<FormControl>
							<Input 
							placeholder="Session ID" 
							value={sessionIdInput} 
							onChange={(e) => setSessionIdInput(e.target.value)} 
							/>
						</FormControl>
						<Button 
							onClick={handleFetchLogs}
							isDisabled={!playerId && !sessionIdInput}
							size="md"
							whiteSpace="nowrap"
							minW="120px"
						>
							Fetch Logs
						</Button>
					</HStack>

					<TableContainer border={'2px'} borderRadius={'5px'} borderColor={'gray.600'} size={'sm'}>
						{/* Removed UserDropdown component */}
						<Table variant="simple">
							<Thead>
								<Tr>
									<Th>Player ID</Th> {/* Changed from User */}
									<Th>Platform</Th>
									<Th>Device Model</Th>
									<Th w="150px" maxW="150px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
										OS
									</Th>
									<Th>Game Version</Th>
									<Th>Build Number</Th>
									<Th>Unity Version</Th>
									<Th>Session Date</Th>
								</Tr>
							</Thead>
							{sessionList.length > 0 &&
								sessionList.map((session) => (
									<React.Fragment key={session.session_id}>
										<Tbody key={`${session.session_id}-body`}>
											<Tr onClick={() => setSelectedSessionId(selectedSessionId === session.session_id ? null : session.session_id)}>
												<Td>{session.player_id || 'N/A'}</Td> {/* Changed from "" */}
												<Td>{getPlatformString(session.platform)}</Td>
												<Td>{session.device_model}</Td>
												<Td w="150px" maxW="150px" isTruncated>
													<Tooltip label={session.os_version} aria-label="Full OS Version">
														{session.os_version}
													</Tooltip>
												</Td>
												<Td>{session.client_version}</Td>
												<Td>{session.build_number}</Td>
												<Td>{session.engine_version}</Td>
												<Td>{convertAndFormatTimestamp(session.session_started_at)}</Td>
											</Tr>
										</Tbody>
										{selectedSessionId === session.session_id && (
											<SelectedSessionRow key={`${session.session_id}-selected`} sessionId={selectedSessionId} />
										)}
									</React.Fragment>
								))}
							{/* Changed condition and text for no sessions found */}
							{sessionList.length === 0 && (playerId || sessionIdInput) && (
								<Tbody>
									<Tr>
										<Td colSpan={9} textAlign="center">
											No sessions found for the specified Player ID / Session ID.
										</Td>
									</Tr>
								</Tbody>
							)}
						</Table>
					</TableContainer>
				</Box>
			</h2>
		</Box>
	);
}

// --- TabbedSessionInfo and other helper functions remain largely the same ---
// Minor changes might be needed if their dependencies were altered, but plan says they are mostly unchanged.

function TabbedSessionInfo({ userToken, selectedSessionId }) {
	const [eventType, setEventType] = useState('All');
	const [filterString, setFilterString] = useState('');
	const [sessionLogList, setSessionLogList] = useState([]);
	const [downloadProgress, setDownloadProgress] = useState(null); // { current: 0, total: 10 }
	const [videoFiles, setVideoFiles] = useState([]); // Store video file metadata
	const [downloadedVideos, setDownloadedVideos] = useState(new Map()); // Map of videoId to decoded frames
	
	// Initialize downloaded archives from sessionStorage to persist across reconnections
	const getPersistedArchives = () => {
		const key = `downloadedArchives_${selectedSessionId}`;
		const stored = sessionStorage.getItem(key);
		if (stored) {
			try {
				return new Set(JSON.parse(stored));
			} catch (e) {
				console.error('Failed to parse persisted archives:', e);
			}
		}
		return new Set();
	};
	
	const [downloadedArchives, setDownloadedArchives] = useState(getPersistedArchives()); // Track downloaded archive IDs
	const downloadedArchivesRef = useRef(downloadedArchives); // Ref to access current downloaded archives
	const downloadingArchivesRef = useRef(new Set()); // Track currently downloading archives
	const lastArchiveListRef = useRef(null); // Track last received archive list to prevent duplicates
	const lastArchiveListTimeRef = useRef(0); // Track when we last processed archives
	
	// Keep ref in sync with state
	useEffect(() => {
		downloadedArchivesRef.current = downloadedArchives;
	}, [downloadedArchives]);
	
	// Persist downloaded archives whenever they change
	useEffect(() => {
		if (selectedSessionId) {
			const key = `downloadedArchives_${selectedSessionId}`;
			sessionStorage.setItem(key, JSON.stringify([...downloadedArchives]));
		}
	}, [downloadedArchives, selectedSessionId]);

	// Function to download all archives automatically
	const downloadAllArchives = async (archives, sessionId) => {
		// Use refs to get current state to avoid stale closure issue
		const currentDownloaded = downloadedArchivesRef.current;
		const currentDownloading = downloadingArchivesRef.current;
		
		// Filter out archives that have already been downloaded or are currently downloading
		const newArchives = archives.filter(archive => 
			!currentDownloaded.has(archive.archive_id) && 
			!currentDownloading.has(archive.archive_id)
		);
		const cachedCount = archives.filter(archive => currentDownloaded.has(archive.archive_id)).length;
		const downloadingCount = archives.filter(archive => currentDownloading.has(archive.archive_id)).length;
		
		if (newArchives.length === 0) {
			console.log(`All ${archives.length} archives are already cached or downloading (${cachedCount} cached, ${downloadingCount} downloading)`);
			// Show brief message that all archives are handled
			const message = downloadingCount > 0 
				? `${downloadingCount} archives downloading, ${cachedCount} cached`
				: 'All archives cached';
			setDownloadProgress({ current: 0, total: 0, cached: cachedCount, downloading: downloadingCount, message });
			setTimeout(() => setDownloadProgress(null), 2000);
			return;
		}
		
		console.log(`Found ${newArchives.length} new archives to download (${cachedCount} cached, ${downloadingCount} downloading, ${archives.length} total)`);
		
		// Mark all new archives as downloading immediately
		newArchives.forEach(archive => {
			downloadingArchivesRef.current.add(archive.archive_id);
		});
		
		console.log(`Archive download state - Downloaded: ${currentDownloaded.size}, Downloading: ${currentDownloading.size + newArchives.length}, New: ${newArchives.length}`);
		
		setDownloadProgress({ current: 0, total: newArchives.length, cached: cachedCount });
		
		for (let i = 0; i < newArchives.length; i++) {
			const archive = newArchives[i];
			try {
				console.log(`Downloading archive ${i + 1}/${newArchives.length}: ${archive.archive_id}`);
				
				await downloadAndDecompressArchive(
					archive.server_id,
					sessionId,
					archive.archive_id,
					(logs) => {
						console.log(`Archive ${archive.archive_id} contained ${logs.length} logs`);
						
						// Immediately update the session log list with new logs (lazy loading)
						setSessionLogList(prevLogs => {
							const mergedLogs = [...prevLogs, ...logs];
							// Remove duplicates based on event_number
							const uniqueLogs = Array.from(
								new Map(mergedLogs.map(log => [log.event_number, log])).values()
							);
							// Sort by event_number
							uniqueLogs.sort((a, b) => a.event_number - b.event_number);
							console.log(`Total logs after adding archive ${archive.archive_id}: ${uniqueLogs.length} (${logs.length} new logs added)`);
							return uniqueLogs;
						});
					}
				);
				
				// Mark this archive as downloaded and remove from downloading
				downloadingArchivesRef.current.delete(archive.archive_id);
				setDownloadedArchives(prev => new Set([...prev, archive.archive_id]));
				
				setDownloadProgress({ current: i + 1, total: newArchives.length, cached: cachedCount });
			} catch (e) {
				console.error(`Failed to download archive ${archive.archive_id}:`, e);
				// Remove from downloading on error too
				downloadingArchivesRef.current.delete(archive.archive_id);
			}
		}
		
		setDownloadProgress(null);
	};

	useEffect(() => {
		// Clear the incomplete cache - it only stored IDs without actual data
		setDownloadedArchives(new Set());
		
		// Clear downloading archives for the new session
		downloadingArchivesRef.current.clear();
		
		// Clear debouncing refs for the new session
		lastArchiveListRef.current = null;
		lastArchiveListTimeRef.current = 0;
		
		// Clear timestamp cache to free memory and avoid stale data
		timestampCache.clear();
		
		// Always start with empty logs
		setSessionLogList([]);
		
		// Clear video-related state from previous session
		setVideoFiles([]);
		setDownloadedVideos(new Map());
		
		let retries = 0;
		let wsInstance; // Renamed to avoid conflict if PlayerLogData's webSocket was in scope

		const connectWebSocketForDetails = () => { // Renamed to be specific
			//const endpoint = 'ws://localhost:8081/ws'; // JWT auth disabled
			const endpoint = 'ws://hub.alictuscloud.com:8081/ws'; // JWT auth disabled
			wsInstance = new WebSocket(endpoint);
			//wsInstance = new WebSocket(`${config.WEBSOCKET_ENDPOINT}?token=${userToken}`); // Original commented line

			wsInstance.onopen = () => {
				retries = 0; 
				if (wsInstance.readyState === WebSocket.OPEN) {
					const subscribeMsg = {
						type: 'subscribe_log_stream',
						payload: { session_id: selectedSessionId },
					};
					wsInstance.send(JSON.stringify(subscribeMsg));
				}
			};

			wsInstance.onmessage = async (event) => {
				let data;
				if (event.data instanceof Blob) {
					const arrayBuffer = await event.data.arrayBuffer();
					const uint8Array = new Uint8Array(arrayBuffer);
					try {
						data = await decompressToString(uint8Array);
					} catch (err) {
						// Fallback: assume the blob already contains a UTF-8 JSON string
						data = new TextDecoder('utf-8').decode(uint8Array);
					}
				} else {
					data = event.data;
				}

				let parsed;
				try {
					parsed = JSON.parse(data);
				} catch (error) {
					console.error('Error parsing incoming data:', error);
					return;
				}

				let incomingLogList = [];
				if (parsed.type === 'log_chunk' && parsed.payload?.logs) {
					incomingLogList = parsed.payload.logs;
				} else if (parsed.type === 'log_event' && parsed.payload?.log) {
					incomingLogList = [parsed.payload.log];
				} else if (parsed.type === 'compressed_archive_list' && parsed.payload?.archives) {
					// Handle compressed archive list with improved debouncing
					const currentTime = Date.now();
					const receivedArchiveIds = new Set(parsed.payload.archives.map(a => a.archive_id));
					
					// Check if this is effectively the same archive list we processed recently
					// Compare sets rather than ordered strings, and use longer window (6 seconds)
					if (lastArchiveListRef.current && 
					    currentTime - lastArchiveListTimeRef.current < 6000) {
						const lastArchiveIds = new Set(lastArchiveListRef.current.split(','));
						// Check if sets are equal
						if (receivedArchiveIds.size === lastArchiveIds.size &&
						    [...receivedArchiveIds].every(id => lastArchiveIds.has(id))) {
							console.log('Ignoring duplicate archive list received within 6 seconds');
							return;
						}
					}
					
					console.log('Received compressed archives:', parsed.payload.archives);
					console.log(`Processing ${parsed.payload.archives.length} archives`);
					
					// Update last processed references
					lastArchiveListRef.current = [...receivedArchiveIds].join(',');
					lastArchiveListTimeRef.current = currentTime;
					
					// Handle video files if present
					if (parsed.payload.video_files && parsed.payload.video_files.length > 0) {
						console.log(`Received ${parsed.payload.video_files.length} video files`);
						setVideoFiles(prevVideos => {
							// Merge new videos with existing ones, avoiding duplicates
							const videoMap = new Map(prevVideos.map(v => [v.file_id, v]));
							parsed.payload.video_files.forEach(video => {
								videoMap.set(video.file_id, video);
							});
							return Array.from(videoMap.values());
						});
					}
					
					// Start downloading all archives automatically
					downloadAllArchives(parsed.payload.archives, selectedSessionId);
					return;
				} else {
					console.warn('Unhandled websocket message:', parsed);
					return;
				}

				setSessionLogList((prevVariables) => {
					const prevArray = Array.isArray(prevVariables) ? prevVariables : [];
					const newLogs = incomingLogList.filter(
						(incomingLog) => !prevArray.some((existingLog) => existingLog.event_number === incomingLog.event_number),
					);
					const mergedLogs = [...prevArray, ...newLogs];
					// Sort by event_number to maintain chronological order
					mergedLogs.sort((a, b) => a.event_number - b.event_number);
					return mergedLogs;
				});
			};

			wsInstance.onclose = (event) => {
				console.log('WebSocket is closed:', event);
				if (event.wasClean === false && retries < 5) {
					retries++;
					console.log(`Reconnecting... Attempt #${retries}`);
					setTimeout(connectWebSocketForDetails, retries * 1000); 
				}
			};

			wsInstance.onerror = (error) => {
				console.error('WebSocket error:', error);
			};
		};

		if (selectedSessionId) { // Only connect if a session is selected
			connectWebSocketForDetails();
		}

		return () => {
			if (wsInstance && (wsInstance.readyState === WebSocket.OPEN || wsInstance.readyState === WebSocket.CONNECTING)) {
				wsInstance.close();
			}
		};
	}, [selectedSessionId]); // userToken is not used for this websocket's auth based on current code

	if (!selectedSessionId) { // If no session is selected, don't show spinner or logs for it
	    return null;
	}

	if (sessionLogList.length === 0) {
		return (
			<Flex justify="center" align="center" minH="200px" direction="column">
				<Spinner size="xl" />
				{downloadProgress && (
					<VStack mt={4} spacing={2}>
						<Text>
							{downloadProgress.message || 
							 `Downloading archives... ${downloadProgress.current}/${downloadProgress.total}`}
						</Text>
						{downloadProgress.cached > 0 && (
							<Text fontSize="sm" color="gray.500">
								({downloadProgress.cached} archives cached)
							</Text>
						)}
					</VStack>
				)}
			</Flex>
		);
	}

	return (
		<>
			<VStack spacing={1} align="stretch">
				{/* Logs are automatically downloaded - no archive buttons needed */}
				{sessionLogList.length > 0 && (
					<>
						<HStack spacing={'10px'} align="center">
							<FormControl paddingTop={'10px'} paddingBottom={'10px'} paddingLeft={'10px'} paddingRight={'10px'}>
								<Select onChange={(selected_event_type) => setEventType(selected_event_type.target.value)}>
									{eventTypes.map((event) => (
										<option key={event.id} value={event.name}>
											{event.name}
										</option>
									))}
								</Select>
							</FormControl>
							<Input placeholder="Filter logs..." onChange={(filter_text) => setFilterString(filter_text.target.value)} />
							{downloadedArchives.size > 0 && (
								<Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
									{downloadedArchives.size} archives
								</Text>
							)}
						</HStack>
						<SessionLogs 
							eventType={eventType} 
							filterString={filterString} 
							sessionLogList={sessionLogList} 
							videoFiles={videoFiles}
							downloadedVideos={downloadedVideos}
							setDownloadedVideos={setDownloadedVideos}
							selectedSessionId={selectedSessionId}
						/>
					</>
				)}
			</VStack>
		</>
	);
}

// Helper function to check if a frame number has video coverage
function isFrameCoveredByVideo(frameNumber, videoFiles) {
	if (!videoFiles || videoFiles.length === 0 || !frameNumber) return false;
	
	return videoFiles.some(videoFile => {
		const frameNumbers = videoFile.meta?.frame_numbers || [];
		if (frameNumbers.length === 0) return false;
		
		// Get min and max frame numbers from the video
		const minFrame = Math.min(...frameNumbers);
		const maxFrame = Math.max(...frameNumbers);
		
		// Check if log frame is within the video's frame range
		return frameNumber >= minFrame && frameNumber <= maxFrame;
	});
}

function SessionLogs({ eventType, filterString, sessionLogList, videoFiles, downloadedVideos, setDownloadedVideos, selectedSessionId }) {
	const [selectedLogInfo, setSelectedLogInfo] = useState(null);
	const [sortOrder, setSortOrder] = useState('desc');
	const { isOpen, onOpen, onClose } = useDisclosure();

	// Memoize filtered results to prevent unnecessary recalculations
	const filteredGameData = useMemo(() => {
		let tempFilterString = filterString.toLowerCase();
		let result = sessionLogList;

		// Filter by event type
		if (eventType !== 'All') {
			if (eventType === 'Analytics') {
				result = result.filter((field) => printEventName(field) !== 'Log');
			} else { // UnityLog
				result = result.filter((field) => printEventName(field) === 'Log');
			}
		}
		
		// Apply text filters
		if (result && tempFilterString.length > 0) {
			let tokens = tempFilterString.split(' ');
			for (let i = 0; i < tokens.length; i++) {
				let token = tokens[i];
				if (token === '' && tokens.length === 1) continue;
				if (token === '-') continue;
				if (token.startsWith('-')) { // Exclusion
					token = token.substring(1);
					if(token === '') continue;
					result = result.filter((field) => !JSON.stringify(field).toLowerCase().includes(token));
				} else { // Inclusion
					result = result.filter((field) => JSON.stringify(field).toLowerCase().includes(token));
				}
			}
		}
		
		// Sort logs based on sortOrder
		if (sortOrder === 'desc') {
			result = [...result].sort((a, b) => b.event_number - a.event_number);
		} else {
			result = [...result].sort((a, b) => a.event_number - b.event_number);
		}
		
		return result;
	}, [sessionLogList, eventType, filterString, sortOrder]); // Only recalculate when these change

	function truncateString(str, num) {
		if (str === null || str === undefined) return ''; // Added undefined check
		if (str.length <= num) {
			return str;
		}
		return str.slice(0, num) + '...';
	}

	return (
		<>
			<style>
				{`
					.log-row:hover {
						background-color: rgba(113, 128, 150, 0.16);
					}
				`}
			</style>
			<TableContainer>
				<OpenDrawer 
					isOpen={isOpen} 
					onClose={onClose} 
					selectedLogInfo={selectedLogInfo} 
					videoFiles={videoFiles}
					downloadedVideos={downloadedVideos}
					setDownloadedVideos={setDownloadedVideos}
					selectedSessionId={selectedSessionId}
				/>
				<Table variant="simple" size="sm" border={'0px'}>
					<Thead>
						<Tr>
							<Th width={'15%'}>Event Name</Th>
							<Th width={'10%'}>Log Type</Th>
							<Th width={'60%'}>Value</Th>
							<Th width={'15%'} cursor="pointer" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
								<HStack spacing={1}>
									<Text>Timestamp</Text>
									<RiArrowUpDownFill />
								</HStack>
							</Th>
						</Tr>
					</Thead>
					<Tbody>
						{filteredGameData &&
							filteredGameData.map((field, i) => (
								<Tr
									key={field.event_number || field.event_id || `${field.timestamp}-${i}`} // Use event_number for logs, event_id for events
									onClick={() => {
										onOpen();
										setSelectedLogInfo(field);
									}}
									className="log-row"
									style={{ cursor: "pointer" }}
								>
									<Td>{printEventName(field)}</Td>
									<Td>{printLogType(field)}</Td>
									<Td>{truncateString(field.log_string || JSON.stringify(field.event_data), 120)}</Td> {/* Handle EventData */}
									<Td>
										<HStack spacing={2}>
											<Text>{convertAndFormatTimestamp(field.timestamp)}</Text>
											{field.frame_number && isFrameCoveredByVideo(field.frame_number, videoFiles) && (
												<Tooltip label={`Frame ${field.frame_number}`} placement="left">
													<Box color="blue.400">
														<FaVideo size={14} />
													</Box>
												</Tooltip>
											)}
										</HStack>
									</Td>
								</Tr>
							))}
					</Tbody>
				</Table>
			</TableContainer>
		</>
	);
}

function OpenDrawer({ isOpen, onClose, selectedLogInfo, videoFiles, downloadedVideos, setDownloadedVideos, selectedSessionId }) {
	const [loadingVideo, setLoadingVideo] = useState(false);
	const [videoError, setVideoError] = useState(null);
	const [targetFrame, setTargetFrame] = useState(null);
	
	// Download and decode video to target frame when drawer opens
	useEffect(() => {
		// Check if we should load video: drawer is open, log has frame number, and frame is covered by video
		const shouldLoadVideo = isOpen && selectedLogInfo?.frame_number && 
			videoFiles && videoFiles.length > 0 && 
			isFrameCoveredByVideo(selectedLogInfo.frame_number, videoFiles);
		
		if (!shouldLoadVideo) {
			setTargetFrame(null);
			setLoadingVideo(false);
			setVideoError(null);
			return;
		}
		
		const decodeToFrame = async () => {
			setLoadingVideo(true);
			setVideoError(null);
			setTargetFrame(null);
			
			try {
				// Import the video decoder
				const { default: videoDecoder } = await import('../wasm/videoDecoder');
				
				// Initialize decoder if needed
				await videoDecoder.init();
				
				// Define download function for video files
				const downloadVideoFile = async (videoFile) => {
					const url = getArchiveDownloadUrl(videoFile.server_id, selectedSessionId, videoFile.file_id);
					if (!url) {
						throw new Error(`No server mapping for server ID ${videoFile.server_id}`);
					}
					
					console.log(`Downloading video ${videoFile.file_id} from: ${url}`);
					const response = await fetch(url);
					if (!response.ok) {
						throw new Error(`HTTP ${response.status}: ${response.statusText}`);
					}
					
					const arrayBuffer = await response.arrayBuffer();
					return new Uint8Array(arrayBuffer);
				};
				
				// Decode to the target frame (handles delta encoding)
				const frame = await videoDecoder.decodeToTargetFrame(
					videoFiles,
					selectedLogInfo.frame_number,
					downloadVideoFile
				);
				
				if (frame) {
					setTargetFrame(frame);
					console.log(`Successfully decoded frame ${frame.targetFrame?.frameNumber || 'not found'} (target was ${selectedLogInfo.frame_number})`);
				} else {
					setVideoError(`Could not find frame ${selectedLogInfo.frame_number} in available videos`);
				}
			} catch (error) {
				console.error('Failed to decode to target frame:', error);
				setVideoError(error.message);
			} finally {
				setLoadingVideo(false);
			}
		};
		
		decodeToFrame();
	}, [isOpen, selectedLogInfo, videoFiles, selectedSessionId]);
	const renderLogInfo = (info, prefix = '') => { // Added prefix for nested keys
		let elements = [];
		Object.entries(info).forEach(([key, value]) => {
			const displayKey = prefix ? `${prefix}.${key}` : key;
			if (key === '_id') return; // Skip _id fields

			if (value !== null && value !== undefined && (typeof value !== 'string' || value.length !== 0)) {
				if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0) { // Expand objects, not arrays directly here
					elements.push(
						<Box key={displayKey} ml={prefix ? 2 : 0} borderLeft={prefix ? "1px solid gray" : "none"} pl={prefix ? 2 : 0}>
							<Heading size="xs" textTransform="uppercase" mt={2}>
								{key}
							</Heading>
							{renderLogInfo(value, key)} {/* Recursive call with new prefix */}
						</Box>
					);
				} else {
					elements.push(
						<Box key={displayKey} ml={prefix ? 4 : 0}> {/* Indent values of nested objects */}
							<Heading size="xs" textTransform="uppercase" mt={prefix && typeof Object.values(info)[0] !== 'object' ? 0 : 2}> {/* Adjust margin for direct children */}
								{key}
							</Heading>
							<Text pt="2" fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-all"> {/* Changed to pre-wrap and break-all */}
								{typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
							</Text>
						</Box>,
					);
				}
			}
		});
		return elements;
	};

	return (
		<>
			<Drawer onClose={onClose} isOpen={isOpen} size={'xl'}> {/* Changed size to xl for video */}
				<DrawerOverlay />
				<DrawerContent>
					<DrawerCloseButton />
					<DrawerBody width={'95%'}>
						<VStack spacing={4} align="stretch">
							{/* Video Player Section - Only show if frame has video coverage */}
							{selectedLogInfo?.frame_number && isFrameCoveredByVideo(selectedLogInfo.frame_number, videoFiles) && (
								<Card>
									<CardBody>
										<Stack spacing={4}>
											<Heading size="md">Video Recording</Heading>
											{loadingVideo && (
												<Flex justify="center" align="center" minH="200px">
													<Spinner size="lg" />
													<Text ml={4}>Loading video frame {selectedLogInfo?.frame_number}...</Text>
												</Flex>
											)}
											{videoError && (
												<Alert status="error">
													<AlertIcon />
													<Box>
														<AlertTitle>Failed to load video</AlertTitle>
														<AlertDescription>{videoError}</AlertDescription>
													</Box>
												</Alert>
											)}
											{targetFrame && !loadingVideo && (
												<>
													<VideoPlayer
														frames={targetFrame.frames}
														metadata={targetFrame.metadata}
														targetFrameNumber={targetFrame.targetFrameNumber}
														targetFrameIndex={targetFrame.targetFrameIndex}
														width={800}
														height={600}
													/>
													<Text fontSize="sm" color="gray.500">
														Frame {targetFrame.targetFrame?.frameNumber || 'N/A'} • 
														Resolution: {targetFrame.metadata.width}x{targetFrame.metadata.height}
													</Text>
												</>
											)}
										</Stack>
									</CardBody>
								</Card>
							)}
							
							{/* Log Details Section */}
							{selectedLogInfo && (
								<Card>
									<CardBody>
										<Stack divider={<StackDivider />} spacing="4">
											<Heading size="md">Log Details</Heading>
											{renderLogInfo(selectedLogInfo)}
										</Stack>
									</CardBody>
								</Card>
							)}
						</VStack>
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</>
	);
}

// Cache for formatted timestamps to avoid repeated date calculations
const timestampCache = new Map();

function convertAndFormatTimestamp(inputTs) {
    if (!inputTs && inputTs !== 0) return 'N/A';
    
    // Check cache first
    const cached = timestampCache.get(inputTs);
    if (cached) return cached;
    
    let date;
    if (typeof inputTs === 'number') {
        // Assume seconds (float) from backend; convert to ms
        date = new Date(inputTs * 1000);
    } else {
        date = new Date(inputTs);
    }

    if (isNaN(date)) return 'N/A';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

    const formatted = `${hours}:${minutes}:${seconds}:${milliseconds} - ${day}/${month}/${year}`;
    
    // Cache the result (limit cache size to prevent memory issues)
    if (timestampCache.size > 10000) {
        // Clear oldest entries when cache gets too large
        const firstKey = timestampCache.keys().next().value;
        timestampCache.delete(firstKey);
    }
    timestampCache.set(inputTs, formatted);
    
    return formatted;
}

function printEventName(log) {
    if (!log) return 'N/A';
    if (log.event_name === null || log.event_name === undefined) {
        return 'Log';
    }
    return log.event_name;
}

function printLogType(log) {
    if (!log) return 'N/A';
    if (log.event_name === null || log.event_name === undefined) {
        return log.log_type ? log.log_type.toLowerCase() : 'log';
    }
    return 'event';
}

function PlayerLogView({ userToken, selectedGameId }) { // Renamed from LogView
	return (
		<Grid
			height={'auto'} // Changed from 300px to auto
			width={'100%'}
			templateRows="auto 1fr" // Adjusted for content
			templateColumns="1fr" // Simplified to single column
			gap={4}
			paddingLeft={'12px'}
			paddingRight={'10px'}
		>
			{/* GridItem for title or global filters could go here if needed */}
			{/* <GridItem colSpan={1} paddingTop={'10px'}></GridItem> */} 

			<GridItem paddingLeft={'5px'} paddingRight={'5px'} colSpan={1}> {/* Adjusted colSpan */}
				{/* Ensure props are passed if PlayerLogData expects them and they are available */}
				<PlayerLogData userToken={userToken} selectedGameId={selectedGameId} />
			</GridItem>
		</Grid>
	);
}

export default PlayerLogView; // Exporting the renamed component